import { db } from '../firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  Timestamp,
  serverTimestamp,
  limit,
  startAfter,
  getDoc,
  writeBatch
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth'; // Add this import
import { format } from 'date-fns';

// Initialize Firebase Auth
const auth = getAuth(); // Add this line

export const dailyLogService = {
  /**
   * Save a new daily log entry
   */
  async saveLogEntry(userId, logData) {
    try {
      // Ensure date is a Firestore timestamp
      const date = logData.date instanceof Date 
        ? Timestamp.fromDate(logData.date)
        : logData.date;
      
      // Create a clean data object for Firestore
      const firestoreData = {
        userId,
        practiceId: logData.practiceId,
        practiceName: logData.practiceName,
        date,
        hours: logData.hours || 0,
        patientCount: logData.patientCount || 0,
        rate: logData.rate || 0,
        description: logData.description || '',
        rateType: logData.rateType || 'percentage',
        rateValue: logData.rateValue || 0,
        invoiced: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      // Add patients data if it exists
      if (logData.patients && Array.isArray(logData.patients) && logData.patients.length > 0) {
        firestoreData.patients = logData.patients;
      }
      
      // Create document in Firestore
      const docRef = await addDoc(collection(db, 'dailyLogs'), firestoreData);
      
      return { id: docRef.id, ...logData };
    } catch (error) {
      console.error('Error saving daily log entry:', error);
      throw error;
    }
  },

  /**
   * Update an existing log entry
   */
  async updateLogEntry(logId, logData) {
    try {
      const logRef = doc(db, 'dailyLogs', logId);
      
      // Ensure date is a Firestore timestamp if it exists
      const updateData = { ...logData, updatedAt: serverTimestamp() };
      if (updateData.date instanceof Date) {
        updateData.date = Timestamp.fromDate(updateData.date);
      }
      
      await updateDoc(logRef, updateData);
      return { id: logId, ...logData };
    } catch (error) {
      console.error('Error updating daily log entry:', error);
      throw error;
    }
  },

  /**
   * Delete a log entry
   */
  async deleteLogEntry(logId) {
    try {
      console.log(`🗑️ Deleting daily log with ID: ${logId}`);
      
      // Directly delete the document using the provided ID
      const logRef = doc(db, 'dailyLogs', logId);
      await deleteDoc(logRef);
      
      console.log(`🗑️ Successfully deleted log: ${logId}`);
      return { success: true };
    } catch (error) {
      console.error('🗑️ Error deleting daily log entry:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Get all log entries for a user within a date range
   */
  async getLogEntries(userId, options = {}) {
    try {
      const { forceRefresh = false } = options;
      
      // Create a base query for user's logs
      let q = query(
        collection(db, 'dailyLogs'),
        where('userId', '==', userId),
        orderBy('date', 'desc')
      );
      
      // Apply date filters if provided
      if (options.startDate) {
        const startTimestamp = Timestamp.fromDate(options.startDate);
        q = query(q, where('date', '>=', startTimestamp));
      }
      
      if (options.endDate) {
        const endTimestamp = Timestamp.fromDate(options.endDate);
        q = query(q, where('date', '<=', endTimestamp));
      }

      // Filter by practice if specified
      if (options.practiceId) {
        q = query(q, where('practiceId', '==', options.practiceId));
      }

      // Filter by invoice status if specified
      if (options.invoiced !== undefined) {
        q = query(q, where('invoiced', '==', options.invoiced));
      }
      
      // Apply pagination if requested
      if (options.pageSize) {
        q = query(q, limit(options.pageSize));
        
        if (options.lastVisible) {
          q = query(q, startAfter(options.lastVisible));
        }
      }
      
      // Execute the query with source option when forceRefresh is true
      let snapshot;
      if (forceRefresh) {
        console.log('🔄 Forcing server read for getLogEntries');
        // Using getDocs with explicit source: 'server' option to bypass cache
        snapshot = await getDocs(q, { source: 'server' });
        console.log(`🔄 Server read complete, got ${snapshot.docs.length} docs`);
      } else {
        snapshot = await getDocs(q);
      }
      
      // Format the results
      const logs = snapshot.docs.map(doc => {
        const data = doc.data();
        
        // Enhanced date conversion with multiple fallbacks
        let formattedDate = null;
        if (data.date) {
          // Case 1: Standard Firestore Timestamp
          if (data.date instanceof Timestamp) {
            formattedDate = data.date.toDate();
          } 
          // Case 2: Raw Firestore timestamp object with _seconds
          else if (data.date._seconds !== undefined) {
            try {
              formattedDate = new Date(data.date._seconds * 1000);
            } catch (e) {
              console.error('Failed to convert timestamp with _seconds:', data.date);
            }
          } 
          // Case 3: Date string that needs parsing
          else if (typeof data.date === 'string') {
            try {
              formattedDate = new Date(data.date);
            } catch (e) {
              console.error('Failed to parse date string:', data.date);
            }
          }
          
          // Final validity check
          if (formattedDate && (isNaN(formattedDate.getTime()) || !isFinite(formattedDate.getTime()))) {
            console.error('Date is invalid after conversion:', formattedDate, 'Original:', data.date);
            formattedDate = null;
          }
        }
        
        // If date is still null, set a fallback (today's date)
        if (!formattedDate) {
          console.warn('Using fallback date for log:', doc.id);
          formattedDate = new Date();
        }
        
        // Ensure we have patients data properly structured
        let patientsData = [];
        if (data.patients && Array.isArray(data.patients)) {
          patientsData = data.patients;
        } else if (data.patientCount && data.rate) {
          // Create a synthetic patient entry if we only have aggregate data
          patientsData = [{
            count: data.patientCount,
            rate: data.rate,
            description: ''
          }];
        }
        
        // CRITICAL FIX: Create a new object and explicitly set the id to be the Firestore document ID
        // This approach ensures it won't be overwritten by the spread operator
        const logEntry = {
          id: doc.id,  // This is the Firestore document ID
          date: formattedDate,
          formattedDate: format(formattedDate, 'dd MMM yyyy'),
          createdAt: data.createdAt ? (data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt._seconds * 1000)) : null,
          updatedAt: data.updatedAt ? (data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(data.updatedAt._seconds * 1000)) : null,
          patients: patientsData
        };

        // Only after setting critical fields, spread the data object
        // If data contains an 'id' field, it will now be stored as data_id to avoid collision
        Object.keys(data).forEach(key => {
          // Skip the 'id' field from data to prevent overwrite
          if (key === 'id') {
            logEntry.data_id = data[key];
          } else if (key !== 'date' && key !== 'createdAt' && key !== 'updatedAt' && key !== 'patients') {
            // Only copy fields we haven't already processed
            logEntry[key] = data[key];
          }
        });

        return logEntry;
      });
      
      // Return results with pagination info
      return {
        logs,
        lastVisible: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null,
        hasMore: snapshot.docs.length === options.pageSize
      };
    } catch (error) {
      console.error('Error retrieving daily logs:', error);
      throw error;
    }
  },

  /**
   * Mark multiple log entries as invoiced
   */
  async markAsInvoiced(logIds, invoiceId) {
    try {
      // Create a batch using the writeBatch function instead of db.batch()
      const batch = writeBatch(db);
      
      logIds.forEach(id => {
        const logRef = doc(db, 'dailyLogs', id);
        batch.update(logRef, { 
          invoiced: true, 
          invoiceId, 
          invoicedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      });
      
      await batch.commit();
      return true;
    } catch (error) {
      console.error('Error marking logs as invoiced:', error);
      throw error;
    }
  },

  /**
   * Get log entry by ID
   */
  async getLogById(logId) {
    try {
      const logRef = doc(db, 'dailyLogs', logId);
      const snapshot = await getDoc(logRef);
      
      if (!snapshot.exists()) {
        throw new Error('Log entry not found');
      }
      
      const data = snapshot.data();
      
      // Convert Firestore timestamp to JS Date
      let formattedDate = null;
      if (data.date) {
        formattedDate = data.date instanceof Timestamp 
          ? data.date.toDate() 
          : new Date(data.date._seconds * 1000);
      }
      
      return {
        id: snapshot.id,
        ...data,
        date: formattedDate,
        formattedDate: formattedDate ? format(formattedDate, 'dd MMM yyyy') : 'Unknown'
      };
    } catch (error) {
      console.error('Error getting log entry by ID:', error);
      throw error;
    }
  },

  /**
   * Archive logs and then delete them from the dailyLogs collection
   * @param {Array<string>} logIds - Array of log IDs to archive and delete
   * @param {string} invoiceId - Invoice ID to associate with archived logs
   * @param {Object} [options] - Options for the archive process 
   * @param {boolean} [options.skipPdfGeneration] - Skip generating PDF for these logs
   * @param {boolean} [options.ignoreRestorationStatus] - Archive even if the log was previously restored
   */
  async archiveAndDeleteLogs(logIds, invoiceId, options = {}) {
    try {
      const { 
        skipPdfGeneration = false,
        ignoreRestorationStatus = true // Default to true - ALWAYS archive regardless of restoration status
      } = options;
      
      console.log(`📦 Starting archiving of ${logIds.length} logs for invoice ${invoiceId}`, {
        options: { skipPdfGeneration, ignoreRestorationStatus }
      });
      
      // Validate all log IDs first to avoid partial operations
      console.log(`📦 Validating ${logIds.length} log IDs`);
      const validationResults = await Promise.all(
        logIds.map(async logId => {
          try {
            const logRef = doc(db, 'dailyLogs', logId);
            const snapshot = await getDoc(logRef);
            if (snapshot.exists()) {
              return { id: logId, valid: true, data: snapshot.data() };
            } else {
              console.warn(`📦 Log ${logId} not found in active collection`);
              return { id: logId, valid: false };
            }
          } catch (error) {
            console.error(`📦 Error validating log ${logId}:`, error);
            return { id: logId, valid: false, error };
          }
        })
      );
      
      // Filter to only valid logs
      const validLogIds = validationResults.filter(result => result.valid).map(result => result.id);
      console.log(`📦 Found ${validLogIds.length} valid logs out of ${logIds.length} requested`);
      
      if (validLogIds.length === 0) {
        console.warn('📦 No valid logs to archive after validation');
        return { 
          success: false, 
          message: 'No valid logs to archive',
          skippedLogs: logIds.filter(id => !validLogIds.includes(id)).map(id => ({ id, reason: 'validation_failed' }))
        };
      }
      
      // Step 1: Get all the logs to archive (only process valid IDs)
      const logsToArchive = [];
      const skippedLogs = [];
      
      // IMPORTANT: Create a new array to store document IDs for deletion
      const documentIdsToDelete = [];
      
      for (const logId of validLogIds) {
        console.log(`📦 Processing log ID: ${logId}`);
        const logRef = doc(db, 'dailyLogs', logId);
        const logSnapshot = await getDoc(logRef);
        
        if (logSnapshot.exists()) {
          const logData = logSnapshot.data();
          
          // Add detailed log data for debugging
          console.log('📦 Document found for archiving:', {
            id: logId,
            userId: logData.userId,
            practiceId: logData.practiceId,
            date: logData.date?.toDate?.() || 'unknown',
            hasRestoreInfo: !!(logData._restoration || logData.restoredFrom)
          });
          
          // Check if this is a restored record and we're not ignoring restoration status
          const isRestored = logData._restoration || logData.restoredFrom;
          
          console.log('📦 Restoration status check:', { 
            isRestored: !!isRestored,
            hasRestoreFrom: !!logData.restoredFrom, 
            hasRestoration: !!logData._restoration,
            willIgnore: ignoreRestorationStatus
          });
          
          if (isRestored && !ignoreRestorationStatus) {
            console.log(`📦 Skipping log ${logId} as it was previously restored`);
            skippedLogs.push({ id: logId, reason: 'previously_restored' });
            continue;
          }
          
          // Remove restoration metadata if present (so the archived copy is clean)
          const { _restoration, restoredFrom, restoredAt, ...cleanLogData } = logData;
          
          // Add to archive list with invoice details and original ID
          logsToArchive.push({
            id: logId, // Store the original ID as a field in the archive document
            originalId: logId, // Add explicit originalId field for clarity
            ...cleanLogData,
            invoiced: true,
            invoiceId,
            archivedAt: serverTimestamp()
          });
          
          // CRITICAL FIX: Store the document ID in our deletion array - this is the key change!
          documentIdsToDelete.push(logId); // This is the actual Firestore document ID
          
          console.log(`📦 Added log ${logId} to archive list`);
        } else {
          console.warn(`📦 Log ${logId} not found for archiving`);
          skippedLogs.push({ id: logId, reason: 'not_found' });
        }
      }
      
      if (logsToArchive.length === 0) {
        console.warn('📦 No logs to archive after filtering');
        return { 
          success: false, 
          message: 'No valid logs to archive',
          skippedLogs
        };
      }
      
      console.log(`📦 Found ${logsToArchive.length} valid logs to archive`);
      
      // Step 2: Create archive documents and track results
      const archiveResults = [];
      
      // Step 3: Add to archive collection
      for (const logData of logsToArchive) {
        // Create a new document in the archive collection
        const archiveRef = await addDoc(collection(db, 'dailyLogsArchive'), logData);
        console.log(`📦 Archived log ${logData.id} to ${archiveRef.id}`);
        archiveResults.push({
          originalId: logData.id,
          archiveId: archiveRef.id
        });
      }
      
      // Step 4: Now delete each document individually with explicit error handling
      console.log(`📦 Starting individual deletion of ${logsToArchive.length} logs`);
      const deleteResults = [];
      
      // CRITICAL FIX: Use the correct document IDs for deletion
      for (let i = 0; i < documentIdsToDelete.length; i++) {
        const docId = documentIdsToDelete[i];
        
        try {
          console.log(`🗑️ Deleting original document with ID: ${docId}`);
          const logRef = doc(db, 'dailyLogs', docId);
          await deleteDoc(logRef);
          console.log(`✅ Successfully deleted document ${docId}`);
          deleteResults.push({ id: docId, success: true });
        } catch (error) {
          console.error(`❌ Error deleting document ${docId}:`, error);
          deleteResults.push({ 
            id: docId, 
            success: false, 
            error: error.message,
            code: error.code
          });
        }
      }
      
      // Check if any deletions failed
      const failedDeletions = deleteResults.filter(result => !result.success);
      if (failedDeletions.length > 0) {
        console.warn(`❌ ${failedDeletions.length} deletions failed:`, failedDeletions);
      }
      
      console.log(`📦 Archive and delete operation completed: ${logsToArchive.length - failedDeletions.length}/${logsToArchive.length} successful`);
      
      return {
        success: true,
        archived: logsToArchive.length,
        deleted: logsToArchive.length - failedDeletions.length,
        deleteFailed: failedDeletions.length,
        skipped: skippedLogs.length,
        archiveResults,
        deleteResults,
        skippedLogs
      };
    } catch (error) {
      console.error('📦 Error archiving and deleting logs:', error);
      throw error;
    }
  },

  /**
   * Get archived log entries for a user within a date range
   */
  async getArchivedLogEntries(userId, options = {}) {
    try {
      // Create a base query for user's archived logs
      let q = query(
        collection(db, 'dailyLogsArchive'),
        where('userId', '==', userId),
        orderBy('date', 'desc')
      );
      
      // Apply date filters if provided
      if (options.startDate) {
        const startTimestamp = Timestamp.fromDate(options.startDate);
        q = query(q, where('date', '>=', startTimestamp));
      }
      
      if (options.endDate) {
        const endTimestamp = Timestamp.fromDate(options.endDate);
        q = query(q, where('date', '<=', endTimestamp));
      }
      
      // Filter by practice if specified
      if (options.practiceId) {
        q = query(q, where('practiceId', '==', options.practiceId));
      }
      
      // Apply pagination if requested
      if (options.pageSize) {
        q = query(q, limit(options.pageSize));
        
        if (options.lastVisible) {
          q = query(q, startAfter(options.lastVisible));
        }
      }
      
      // Debug logging for this query
      console.log('🔍 Running archive query with options:', options);
      
      const snapshot = await getDocs(q);
      console.log(`📦 Archive query returned ${snapshot.docs.length} logs`);
      
      // Format the results (similar to getLogEntries)
      const logs = snapshot.docs.map(doc => {
        const data = doc.data();
        
        // Enhanced date conversion with multiple fallbacks - same as in getLogEntries
        let formattedDate = null;
        if (data.date) {
          // Case 1: Standard Firestore Timestamp
          if (data.date instanceof Timestamp) {
            formattedDate = data.date.toDate();
          } 
          // Case 2: Raw Firestore timestamp object with _seconds
          else if (data.date._seconds !== undefined) {
            try {
              formattedDate = new Date(data.date._seconds * 1000);
            } catch (e) {
              console.error('Failed to convert timestamp with _seconds:', data.date);
            }
          } 
          // Case 3: Date string that needs parsing
          else if (typeof data.date === 'string') {
            try {
              formattedDate = new Date(data.date);
            } catch (e) {
              console.error('Failed to parse date string:', data.date);
            }
          }
          
          // Final validity check
          if (formattedDate && (isNaN(formattedDate.getTime()) || !isFinite(formattedDate.getTime()))) {
            console.error('Date is invalid after conversion:', formattedDate, 'Original:', data.date);
            formattedDate = null;
          }
        }
        
        // If date is still null, set a fallback (today's date)
        if (!formattedDate) {
          console.warn('Using fallback date for log:', doc.id);
          formattedDate = new Date();
        }
        
        // Ensure we have patients data properly structured
        let patientsData = [];
        if (data.patients && Array.isArray(data.patients)) {
          patientsData = data.patients;
        } else if (data.patientCount && data.rate) {
          // Create a synthetic patient entry if we only have aggregate data
          patientsData = [{
            count: data.patientCount,
            rate: data.rate,
            description: ''
          }];
        }
        
        return {
          id: doc.id,
          originalId: data.id, // Keep track of the original ID
          date: formattedDate,
          formattedDate: format(formattedDate, 'dd MMM yyyy'),
          createdAt: data.createdAt ? 
            (data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt._seconds * 1000)) : 
            null,
          updatedAt: data.updatedAt ? 
            (data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(data.updatedAt._seconds * 1000)) : 
            null,
          archivedAt: data.archivedAt ? 
            (data.archivedAt instanceof Timestamp ? data.archivedAt.toDate() : new Date(data.archivedAt._seconds * 1000)) : 
            null,
          patients: patientsData,
          isArchived: true,
          // Copy remaining fields only if they aren't already processed
          ...Object.fromEntries(
            Object.entries(data).filter(([key]) => 
              !['id', 'date', 'createdAt', 'updatedAt', 'archivedAt', 'patients'].includes(key)
            )
          )
        };
      });
      
      // Return results with pagination info
      return {
        logs,
        lastVisible: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null,
        hasMore: snapshot.docs.length === options.pageSize
      };
    } catch (error) {
      console.error('Error retrieving archived logs:', error);
      throw error;
    }
  },
  
  /**
   * Clean up old archived logs
   * @param {string} userId - User ID
   * @param {Date} olderThan - Delete logs older than this date
   * @param {number} batchSize - Number of logs to process at once
   */
  async cleanupOldArchivedLogs(userId, olderThan, batchSize = 100) {
    try {
      console.log(`Starting cleanup of archived logs older than ${olderThan.toISOString()}`);
      const thresholdTimestamp = Timestamp.fromDate(olderThan);
      
      // Query for old archives
      let q = query(
        collection(db, 'dailyLogsArchive'),
        where('userId', '==', userId),
        where('date', '<', thresholdTimestamp),
        limit(batchSize)
      );
      
      let totalDeleted = 0;
      let hasMore = true;
      
      // Loop until we've processed all matching documents
      while (hasMore) {
        const snapshot = await getDocs(q);
        
        if (snapshot.empty) {
          hasMore = false;
          break;
        }
        
        // Create a new batch for each group
        const batch = writeBatch(db);
        
        // Add deletes to the batch
        snapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
          totalDeleted++;
        });
        
        // Commit the batch
        await batch.commit();
        console.log(`Deleted batch of ${snapshot.docs.length} old archived logs`);
        
        // Check if we need to continue
        if (snapshot.docs.length < batchSize) {
          hasMore = false;
        } else {
          // Create a new query starting after the last doc in this batch
          q = query(
            collection(db, 'dailyLogsArchive'),
            where('userId', '==', userId),
            where('date', '<', thresholdTimestamp),
            startAfter(snapshot.docs[snapshot.docs.length - 1]),
            limit(batchSize)
          );
        }
      }
      
      console.log(`Finished cleanup. Total logs deleted: ${totalDeleted}`);
      return totalDeleted;
    } catch (error) {
      console.error('Error cleaning up old archived logs:', error);
      throw error;
    }
  },

  /**
   * Unarchive a log entry with improved fallback search
   * @param {string} archiveId - ID of the archived log to restore
   */
  async unarchiveLog(archiveId) {
    // Create a detailed log object to track all steps
    const debugLog = {
      steps: [],
      errors: []
    };
    
    const logStep = (step, data = null) => {
      console.log(`UNARCHIVE STEP: ${step}`, data || '');
      debugLog.steps.push({ step, timestamp: new Date().toISOString(), data });
    };
    
    const logError = (step, error) => {
      const errorInfo = {
        step,
        message: error.message,
        code: error.code,
        timestamp: new Date().toISOString()
      };
      console.error(`UNARCHIVE ERROR at ${step}:`, errorInfo);
      debugLog.errors.push(errorInfo);
    };
    
    try {
      logStep('Start unarchive process', { archiveId });
      
      // Step 1: Get current user
      const auth = getAuth();
      const currentUser = auth.currentUser;
      
      if (!currentUser) {
        const authError = new Error('Authentication required');
        logError('Authentication check', authError);
        throw authError;
      }
      
      logStep('Got current user', { uid: currentUser.uid });
      
      // Step 2: Get the archived log data with comprehensive search strategy
      let archiveData = null;
      let archiveSnapshot = null;
      let archiveDocId = archiveId;
      
      try {
        // Try multiple lookup methods to find the archived document
        const lookupResult = await this.findArchivedDocument(archiveId, currentUser.uid, debugLog);
        
        if (!lookupResult.found) {
          throw new Error(`Archived log entry not found after trying all lookup methods. Original ID: ${archiveId}`);
        }
        
        archiveSnapshot = lookupResult.snapshot;
        archiveDocId = lookupResult.docId;
        archiveData = lookupResult.data;
        
        logStep('Retrieved archive data via search', { 
          searchMethod: lookupResult.method,
          docId: archiveDocId,
          docUserId: archiveData.userId,
          practiceId: archiveData.practiceId 
        });
      } catch (error) {
        // Add more detailed error info
        error.additionalInfo = {
          collectionPath: 'dailyLogsArchive',
          attemptedId: archiveId,
          userId: currentUser.uid
        };
        logError('Archive data retrieval', error);
        throw new Error(`Failed to read archive: ${error.message}`);
      }
      
      // Step 3: Verify ownership
      if (archiveData.userId !== currentUser.uid) {
        const ownershipError = new Error('Permission denied: You can only restore your own records');
        logError('Ownership verification', ownershipError);
        throw ownershipError;
      }
      
      logStep('Verified ownership');
      
      // Step 4: Prepare data for new document
      // Remove archive-specific fields and add restoration metadata
      const { archivedAt, originalId, invoiceId, invoiced, restored, restoredAt, ...restoredData } = archiveData;
      
      restoredData.userId = currentUser.uid; // Ensure correct user ID
      restoredData.invoiced = false; // Reset invoice status (THIS IS CORRECT - set to false to make it active again)
      restoredData.updatedAt = serverTimestamp();
      restoredData.restoredFrom = archiveDocId;
      restoredData.restoredAt = serverTimestamp();
      restoredData._restoration = { 
        timestamp: new Date().toISOString(),
        source: 'unarchiveLog',
        userId: currentUser.uid,
        archiveDocId
      };
      
      logStep('Prepared restored data');
      
      // Step 5: Create new active log document
      let newLogRef = null;
      let newLogId = null;
      try {
        newLogRef = await addDoc(collection(db, 'dailyLogs'), restoredData);
        newLogId = newLogRef.id;
        logStep('Created new log document', { newLogId });
      } catch (error) {
        logError('Creating new log document', error);
        throw new Error(`Failed to create restored log: ${error.message}`);
      }
      
      // Step 6: DELETE the archive document (changed from update to delete)
      try {
        const archiveRef = doc(db, 'dailyLogsArchive', archiveDocId);
        await deleteDoc(archiveRef);
        logStep('Deleted archive document');
      } catch (error) {
        // Don't fail the whole operation if just the archive deletion fails
        logError('Deleting archive document', error);
        console.warn('Archive deletion failed but restoration completed successfully');
      }
      
      logStep('Restoration completed successfully');
      
      // Return success result with both logs
      return {
        success: true,
        debugLog,
        newLogId,
        archiveId: archiveDocId,
        restored: {
          userId: currentUser.uid,
          practiceId: archiveData.practiceId,
          originalLogId: originalId || null
        }
      };
    } catch (error) {
      // Log the complete error for debugging
      console.error('Complete unarchive error:', error);
      if (error.code) {
        console.error('Firebase error code:', error.code);
      }
      
      // Capture the full error state
      debugLog.finalError = {
        message: error.message,
        code: error.code,
        timestamp: new Date().toISOString(),
        stack: error.stack
      };
      
      // Return a detailed error with debug information
      const enhancedError = new Error(`Unarchive failed: ${error.message}`);
      enhancedError.debugLog = debugLog;
      enhancedError.code = error.code;
      enhancedError.originalError = error;
      
      throw enhancedError;
    }
  },
  
  /**
   * Comprehensive search for archived document using multiple methods
   * @param {string} archiveId - ID to search for
   * @param {string} userId - Current user's ID
   * @param {Object} debugLog - Log object for tracking
   * @returns {Object} - Search result with document data
   */
  async findArchivedDocument(archiveId, userId, debugLog) {
    const logStep = (step, data = null) => {
      console.log(`ARCHIVE SEARCH: ${step}`, data || '');
      if (debugLog && debugLog.steps) {
        debugLog.steps.push({ step, timestamp: new Date().toISOString(), data });
      }
    };
    
    // Method 1: Direct lookup by document ID
    logStep('Trying direct document lookup', { id: archiveId });
    try {
      const archiveRef = doc(db, 'dailyLogsArchive', archiveId);
      const snapshot = await getDoc(archiveRef);
      
      if (snapshot.exists()) {
        logStep('Document found by direct ID lookup', { id: snapshot.id });
        return {
          found: true,
          method: 'direct_id',
          snapshot,
          docId: snapshot.id,
          data: snapshot.data()
        };
      } else {
        logStep('Document not found by direct lookup, trying alternatives');
      }
    } catch (error) {
      logStep('Error in direct lookup', { error: error.message });
    }
    
    // Method 2: Query by ID field in document data
    logStep('Trying query by id field', { userId, idToMatch: archiveId });
    try {
      // Query for documents where the id field matches our target
      const idQuery = query(
        collection(db, 'dailyLogsArchive'),
        where('userId', '==', userId),
        where('id', '==', archiveId),
        limit(1)
      );
      
      const idQuerySnapshot = await getDocs(idQuery);
      
      if (!idQuerySnapshot.empty) {
        const doc = idQuerySnapshot.docs[0];
        logStep('Document found by id field query', { id: doc.id });
        return {
          found: true,
          method: 'id_field',
          snapshot: doc,
          docId: doc.id,
          data: doc.data()
        };
      } else {
        logStep('No documents found by id field query');
      }
    } catch (error) {
      logStep('Error in id field query', { error: error.message });
    }
    
    // Method 3: Query by originalId field
    logStep('Trying query by originalId field', { userId, originalIdToMatch: archiveId });
    try {
      const originalIdQuery = query(
        collection(db, 'dailyLogsArchive'),
        where('userId', '==', userId),
        where('originalId', '==', archiveId),
        limit(1)
      );
      
      const originalIdQuerySnapshot = await getDocs(originalIdQuery);
      
      if (!originalIdQuerySnapshot.empty) {
        const doc = originalIdQuerySnapshot.docs[0];
        logStep('Document found by originalId field query', { id: doc.id });
        return {
          found: true,
          method: 'original_id_field',
          snapshot: doc,
          docId: doc.id,
          data: doc.data()
        };
      } else {
        logStep('No documents found by originalId field query');
      }
    } catch (error) {
      logStep('Error in originalId field query', { error: error.message });
    }
    
    // Method 4: List recent archives to help with debugging
    logStep('Listing recent archives for debugging', { userId });
    try {
      const recentQuery = query(
        collection(db, 'dailyLogsArchive'),
        where('userId', '==', userId),
        orderBy('archivedAt', 'desc'),
        limit(5)
      );
      
      const recentSnapshot = await getDocs(recentQuery);
      
      if (!recentSnapshot.empty) {
        const recentDocs = recentSnapshot.docs.map(doc => ({
          id: doc.id,
          originalId: doc.data().originalId,
          archivedAt: doc.data().archivedAt?.toDate?.() || 'unknown',
          invoiceId: doc.data().invoiceId
        }));
        
        logStep('Recent archives found:', { count: recentSnapshot.size, samples: recentDocs });
      } else {
        logStep('No recent archives found for this user');
      }
    } catch (error) {
      logStep('Error listing recent archives', { error: error.message });
    }
    
    // No matching document found after trying all methods
    return {
      found: false,
      method: 'none'
    };
  },

  // ...existing code...
};
